import { getCountries, getCountryCallingCode, type CountryCode } from "libphonenumber-js";

export interface PhoneCountry {
  iso: CountryCode;
  name: string;
  dialCode: string; // "+44"
  flag: string;
}

/** Emoji flag from an ISO 3166-1 alpha-2 code (regional indicator pair). */
export function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65));
}

function countryName(iso: string): string {
  try {
    const names = new Intl.DisplayNames(["en"], { type: "region" });
    return names.of(iso) || iso;
  } catch {
    return iso;
  }
}

let cached: PhoneCountry[] | null = null;

/**
 * Every country libphonenumber has dialling metadata for, sorted by name.
 * Keyed by ISO code, never by dial code — "+1" alone is shared by the US,
 * Canada and ~20 Caribbean countries, and "+7" by Russia and Kazakhstan.
 */
export function getPhoneCountries(): PhoneCountry[] {
  if (cached) return cached;
  cached = getCountries()
    .map((iso) => ({
      iso,
      name: countryName(iso),
      dialCode: `+${getCountryCallingCode(iso)}`,
      flag: flagEmoji(iso),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return cached;
}

/** Best guess at the visitor's country from browser locale; US if none. */
export function detectDefaultCountry(): CountryCode {
  const supported = new Set<string>(getCountries());
  const langs = typeof navigator !== "undefined" ? navigator.languages || [navigator.language] : [];
  for (const lang of langs) {
    const region = lang?.split("-")[1]?.toUpperCase();
    if (region && supported.has(region)) return region as CountryCode;
  }
  return "US";
}
